"use client";

import { useEffect, useState } from "react";
import Modal from "react-modal";
import { X, Car } from "lucide-react";
import {
  useGetVehicletype,
  useGetDriverClaimMutation,
  useReserveBookingMutation,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import { toast } from "react-toastify";
interface RowData {
  master_package_id?: number;
  vehicle_type_id?: number;
  booking_amount?: number;
  city_id?: number;
  // Add more fields if needed
}

interface ClaimBookingModalProps {
  isOpen: boolean;
  bookingId: string | number;
  onClose: () => void;
  rowData: RowData;
}

const ClaimBookingModal = ({
  isOpen,
  bookingId,
  onClose,
  rowData,
}: ClaimBookingModalProps) => {
  const [driverType, setDriverType] = useState<string>("");
  const user = useAuth.getState().user;
  const { data: vehicleTypeData, isLoading } = useGetVehicletype();
  const { mutate: driverMutation, data: driverData } =
    useGetDriverClaimMutation(user?.id, user?.user_type_id, driverType);
  const [selectedDriverId, setSelectedDriverId] = useState<string | number>("");
  const { mutate: reserveBookingMutation, data: reserveBookingData } =
    useReserveBookingMutation();
  useEffect(() => {
    if (typeof window !== "undefined") {
      const nextApp = document.getElementById("__next");
      if (nextApp) Modal.setAppElement(nextApp);
    }
  }, []);
  const onReserveClick = () => {
    if (!selectedDriverId) {
      toast.error("Please select a driver", {
        position: "top-right",
        autoClose: 5000,
      });
      return;
    }
    reserveBookingMutation({
      booking_id: bookingId,
      driver_id: selectedDriverId,
    });
    toast.success("Booking accepted successfully");
    onClose()
  };
  const onDriverTypeComboHandler = (val: string) => {
    setDriverType(val);
    if (val) {
      driverMutation({
        master_package_id: rowData?.master_package_id,
        vehicle_type_id: rowData?.vehicle_type_id,
        booking_amount: rowData?.booking_amount,
        city_id: rowData?.city_id,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      overlayClassName="fixed inset-0 bg-transparent bg-opacity-40 z-50 flex items-center justify-center"
      className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-hidden focus:outline-none"
      contentLabel="Claim Booking Modal"
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-2">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Car size={18} /> Claim Booking
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-red-500 transition"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto space-y-6 mt-4 pr-2">
          {/* Booking ID */}
          <div className="text-sm text-gray-600">
            Booking Ref ID:{" "}
            <span className="font-semibold text-gray-800">{bookingId}</span>
          </div>

          {/* Driver Type */}
          <div>
            <label
              htmlFor="driver-type"
              className="block mb-1 text-sm font-medium text-gray-700"
            >
              Select Driver Type
            </label>
            <select
              id="driver-type"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => onDriverTypeComboHandler(e?.target?.value)}
            >
              <option value="">Select Option</option>
              <option value="All">All</option>
              <option value="Online-Active">Online-Active</option>
              <option value="Online-InActive">Online-InActive</option>
              <option value="Offline-Active">Offline-Active</option>
              <option value="Offline-InActive">Offline-InActive</option>
            </select>
          </div>

          {/* Driver */}
          {driverData && (
            <div>
              <label
                htmlFor="driver"
                className="block mb-1 text-sm font-medium text-gray-700"
              >
                Select Driver
              </label>
              <select
                id="driver"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => setSelectedDriverId(e.target.value)}
              >
                <option value="">Select Option</option>
                {/* Dynamic driver list */}
                {driverData?.map((driver: any) => (
                  <option key={driver.user_id} value={driver.user_id}>
                    {" "}
                    {driver.first_name + " " + driver.last_name} (
                    {driver.mobile}) {driver.vehicle_model_name}{" "}
                    {driver.vehicle_name} {driver.vehicle_no}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Scrollable Table Area */}
          <div className="border rounded-lg overflow-hidden max-h-48 overflow-y-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-3 py-2">Car Name</th>
                  <th className="px-3 py-2">Color</th>
                </tr>
              </thead>
              <tbody>
                {vehicleTypeData?.map((item: any, index: number) => (
                  <tr
                    key={index}
                    className="border-t hover:bg-gray-50 transition duration-150"
                  >
                    <td className="px-3 py-1">{item?.vehicle_type}</td>
                    <td className="px-3 py-1">
                      <div
                        className="inline-block w-6 h-3 border rounded"
                        style={{ backgroundColor: item?.color }}
                        title={item?.color}
                      ></div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 pt-4 border-t mt-4">
          <button
            onClick={() => onReserveClick()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition"
          >
            Reserve Booking
          </button>
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg text-sm transition"
          >
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ClaimBookingModal;
